import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** Imagen en grande (póster del torneo, logo del club) con «Descargar» y
 * «Cerrar» (Eduardo 2026-10-05, inicio del torneo del jugador). Mismo
 * visor que el póster del link público (`.poster-lightbox`): se cierra con
 * Esc, con el fondo o con la ×; el foco entra al visor, no se sale con Tab y
 * al cerrar regresa a lo que lo abrió. */
export default function ImageLightbox({
  src,
  alt,
  title,
  downloadLabel = "Descargar",
  onClose,
}: {
  src: string;
  alt: string;
  title: string;
  downloadLabel?: string;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"),
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      opener?.focus();
    };
  }, []);

  return createPortal(
    <div className="poster-lightbox poster-lightbox--over-chrome" role="dialog" aria-modal="true" aria-label={title}>
      <div className="poster-lightbox__backdrop" onClick={onClose} />
      <div className="poster-lightbox__panel" ref={panelRef}>
        <img src={src} alt={alt} />
        <div className="poster-lightbox__actions">
          <a className="button button--primary" href={src} download target="_blank" rel="noreferrer">
            {downloadLabel}
          </a>
          <button type="button" className="button button--secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
        <button ref={closeRef} type="button" className="poster-lightbox__close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>
      </div>
    </div>,
    document.body,
  );
}
